<template>
    <main-layout class="bg-gray-100 min-h-screen flex flex-col">
        <!-- Bandeau dégradé + header -->
        <div class="bg-gradient-to-b from-secondary to-primary">
            <app-header icon="factory" class="!hidden">Détenteur</app-header>

            <header class="text-white px-4 pt-4 pb-12 flex items-center justify-between">
                <!-- Titre + retour -->
                <h1 class="text-2xl flex items-center gap-3">
                    <router-link
                        class="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-white/15 hover:bg-white/25 transition"
                        :to="{ name: 'liste-detenteurs' }">
                        <i class="inline-block h-10 w-10 mask mask-arrow-left bg-white"></i>
                    </router-link>
                    <span>Détenteur</span>
                </h1>

                <!-- Action “Intervenir” si technicien -->
                <router-link v-if="typeUser === 'technicien'"
                    class="inline-flex items-center bg-white transition px-4 py-2 rounded-lg text-primary text-sm font-medium w-auto"
                    :to="{ name: 'new-fibsdd', query: { detenteur: $route.params.id } }" id="main-action">
                    Intervenir
                </router-link>

            </header>
        </div>

        <!-- Corps arrondi + dégradé clair -->
        <main class="bg-gradient-to-b from-[#F8FBFF] to-[#E2E9F5] rounded-t-3xl -mt-6 flex-1 overflow-y-auto shadow-xl">
            <div class="m-2 shadow-2xl rounded-lg overflow-hidden mb-24 relative">
                <!-- Bouton position flottant en haut à droite -->
                <button type="button"
                    class="absolute top-3 right-3 inline-flex items-center justify-center h-10 w-10 rounded-full bg-primary text-white shadow hover:shadow-md border border-primary"
                    @click="getPosition()" title="Enregistrer la localisation">
                    <i class="inline-block h-6 w-6 mask mask-map-solid bg-white"></i>
                </button>

                <!-- Carte formulaire -->
                <div class="bg-white p-4">
                    <form id="detenteur" class="space-y-6" @submit.prevent="sendForm">
                        <!-- Informations générales -->
                        <div>
                            <h2 class="text-lg font-medium mb-4 uppercase text-secondary">Informations générales</h2>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <!-- Type -->
                                <div>
                                    <label class="label">Type</label>
                                    <select v-model="detenteur.type" class="input">
                                        <option disabled>Sélectionnez un type de détenteur</option>
                                        <option>Société</option>
                                        <option>Particulier</option>
                                        <option>Association</option>
                                    </select>
                                </div>

                                <!-- Code client -->
                                <div>
                                    <label class="label">Code client</label>
                                    <input title="clientCode" id="code_client" type="text"
                                        v-model.trim="detenteur.code_client" class="input" />
                                </div>

                                <!-- Raison sociale / Nom -->
                                <div class="md:col-span-2">
                                    <label class="label">
                                        Raison sociale / Nom <span class="text-alert">*</span>
                                    </label>
                                    <input title="company" id="raison_sociale" type="text"
                                        v-model.trim="detenteur.raison_sociale" readonly
                                        class="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-3 text-sm text-gray-700" />
                                </div>

                                <!-- SIRET (masqué pour particulier) -->
                                <div v-show="detenteur.type !== 'Particulier'">
                                    <label class="label">SIRET</label>
                                    <cleave ref="siret" :options="siretOpt" v-model.trim="detenteur.siret"
                                        class="input" />
                                </div>
                            </div>

                            <!-- Adresse / CP / Ville -->
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <!-- Adresse -->
                                <div class="md:col-span-2">
                                    <label class="label">
                                        Adresse <span class="text-alert">*</span>
                                    </label>
                                    <input title="address" id="adresse" type="text" maxlength="255"
                                        v-model.trim="detenteur.adresse" class="input" />
                                </div>

                                <!-- Complément d'adresse -->
                                <div class="md:col-span-2">
                                    <label class="label">Complément d'adresse</label>
                                    <input title="moreAddress" id="complement_adresse" type="text" maxlength="255"
                                        v-model.trim="detenteur.complement_adresse" class="input" />
                                </div>

                                <!-- Code postal -->
                                <div>
                                    <label class="label">
                                        Code postal <span class="text-alert">*</span>
                                    </label>
                                    <cleave ref="cp" :options="cpOpt" v-model.trim="detenteur.code_postal"
                                        class="input" />
                                </div>

                                <!-- Ville -->
                                <div>
                                    <label class="label">
                                        Ville <span class="text-alert">*</span>
                                    </label>
                                    <input title="city" id="adresse_ville" type="text" maxlength="255"
                                        v-model.trim="detenteur.ville" class="input" />
                                </div>
                            </div>

                            <!-- Téléphone / Email -->
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <!-- Téléphone -->
                                <div>
                                    <label class="label">Téléphone</label>
                                    <cleave ref="telephone" :options="telOpt" v-model.trim="detenteur.telephone"
                                        inputmode="tel" class="input" />
                                </div>

                                <!-- Email -->
                                <div>
                                    <label class="label">
                                        Email <span class="text-[11px] text-gray-500">(réception des fiches)</span>
                                    </label>
                                    <input title="email" id="email" type="text" maxlength="255"
                                        v-model.trim="detenteur.email" class="input" />
                                </div>
                            </div>
                        </div>

                        <!-- Contact -->
                        <div>
                            <h2 class="text-lg font-medium mb-4 uppercase text-secondary">Contact</h2>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <!-- Nom / Prénom -->
                                <div class="md:col-span-2">
                                    <label class="label">Nom / Prénom</label>
                                    <input title="fullName" id="nom_contact" type="text" maxlength="255"
                                        v-model.trim="detenteur.nom" class="input" />
                                </div>

                                <!-- Portable -->
                                <div>
                                    <label class="label">Téléphone portable</label>
                                    <cleave ref="portable" :options="telOpt" v-model.trim="detenteur.portable"
                                        inputmode="tel" class="input" />
                                </div>

                                <!-- Ligne directe -->
                                <div>
                                    <label class="label">Ligne directe</label>
                                    <cleave ref="lignedirecte" :options="telOpt" v-model.trim="detenteur.direct"
                                        inputmode="tel" class="input" />
                                </div>
                            </div>
                        </div>

                        <!-- Référence interne (readonly) -->
                        <div>
                            <label class="label">Référence interne</label>
                            <input title="ref" id="custom" type="text" v-model.trim="detenteur.custom" readonly
                                class="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-3 text-sm text-gray-700" />
                        </div>

                        <!-- Submit -->
                        <div class="pt-2">
                            <button v-if="canEditDetenteurs" type="submit" name="update"
                                class="w-full sm:w-auto bg-primary text-white font-semibold tracking-wide px-5 py-3 rounded-xl shadow hover:shadow-md active:scale-[0.99] transition">
                                SAUVEGARDER
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </main>
    </main-layout>
</template>

<script>

import Operateur from "@/js/models/Operateur";
import Cleave from 'vue-cleave';
import Detenteur from '../../js/models/Detenteur';

require('cleave.js/dist/addons/cleave-phone.fr');

export default {
    data() {
        return {
            typeUser: localStorage.getItem('typeUser'),
            siret: '',
            detenteur: {},
            siretOpt: {
                blocks: [3, 3, 3, 5],
                delimiter: ' ',
                numericOnly: true,
            },
            cpOpt: {
                blocks: [2, 3, 20],
                delimiter: ' ',
                numericOnly: false
            },
            telOpt: {
                phone: true,
                phoneRegionCode: 'fr'
            },
            canEditDetenteurs: null,
            havOptionCustom: false
        }
    },
    methods: {
        fetchDetenteur() {
            let idDet = this.$route.params.id;
            let vueInstance = this;

            Detenteur
                .getDetenteur(idDet)
                .then(det => {
                    vueInstance.detenteur = det;
                    vueInstance.$refs.cp.cleave.element.value = det.code_postal;

                    if (det.siret) {
                        vueInstance.$refs.siret.cleave.element.value = det.siret
                    }

                    if (det.telephone) {
                        vueInstance.$refs.telephone.cleave.element.value = det.telephone
                    }

                    if (det.portable) {
                        vueInstance.$refs.portable.cleave.element.value = det.portable
                    }

                    if (det.direct) {
                        vueInstance.$refs.lignedirecte.cleave.element.value = det.direct
                    }
                })
        },
        async getPosition() {
            if (this.detenteur.lat && this.detenteur.lng) {
                swal({
                    title: "Enregistrer la localisation",
                    text: "Position du détenteur déjà définie, voulez vous la modifier ?",
                    type: "question",
                    confirmButtonText: "Modifier",
                    cancelButtonText: "Supprimer",
                    showCancelButton: true,
                }).then(async (result) => {
                    if (result == true) {
                        await this.askPosition();
                    }
                }, (result) => {
                    if (result == 'cancel') {
                        this.detenteur.lat = null;
                        this.detenteur.lng = null;
                        this.saveDetenteur(false);
                    }
                });
            } else {
                await this.askPosition();
            }
        },
        async askPosition() {
            swal({
                title: "Enregistrer la localisation",
                text: "Voulez vous positionner le détenteur à votre position ?",
                type: "question",
                confirmButtonText: "Oui",
                cancelButtonText: "Non",
                showCancelButton: true,
            }).then(async (res) => {
                if (res == true) {
                    if (window.isMobile()) {
                        if (typeof window.flutter_inappwebview !== 'undefined' && window.flutter_inappwebview !== null) {
                            let result = await window.flutter_inappwebview.callHandler('requestGeolocation');

                            if (result !== false) {
                                this.detenteur.lat = result.latitude;
                                this.detenteur.lng = result.longitude;

                                this.saveDetenteur();
                            } else {
                                swal({
                                    title: "Erreur",
                                    text: "La position du détenteur n'a pas été enregistrée.",
                                    type: "error",
                                });
                            }
                        }
                    }
                }
            });
        },
        sendForm() {
            let router = this.$router;
            let newDet = this.$data.detenteur;

            let errors = [];
            let txt = '';

            if (newDet.raison_sociale === "") {
                errors.push('la raison sociale / Nom')
            }
            if (newDet.adresse === "") {
                errors.push("l'adresse")
            }
            if (newDet.ville === "") {
                errors.push('la ville')
            }
            if (newDet.code_postal === "") {
                errors.push('le code postal')
            }

            if (errors.length !== 0) {
                txt += 'Veuillez renseigner ';
                txt += errors.join(', ')
            }

            if (newDet.siret) {
                if ((newDet.siret.length !== 17 && newDet.siret.length !== 14) && newDet.siret.length !== 0) {
                    txt += '<br/>SIRET invalide (14 chiffres)'
                }
            }

            if (txt !== '') {
                swal('Information(s) manquante(s) :', txt, 'error')
            } else {
                this.saveDetenteur(true);
            }
        },
        saveDetenteur(goToList = false) {
            let newDet = this.$data.detenteur;

            delete newDet.cpOpt;
            delete newDet.siretOpt;
            delete newDet.telOpt;

            Detenteur.updateDetenteur(newDet).then(() => {
                if (goToList) {
                    let router = this.$router;
                    router.push({ name: 'liste-detenteurs' });
                }
                swal('Détenteur modifié', 'Le détenteur a bien été modifié', 'success')
            })
        }
    },
    mounted: async function () {
        this.canEditDetenteurs = JSON.parse(localStorage.getItem('canEditDetenteurs'));
        this.fetchDetenteur()

        let options = await Operateur.getOperateurOptions();
        options.includes("customIdDetenteur") ? this.haveOption = true : this.haveOption = false;
    },
    components: {
        Cleave
    }
}
</script>



// WEBPACK FOOTER //
// Detenteur.vue?6ef8a6fe